import { Box, GlobalStyles, SxProps, Theme, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Lightbox from 'react-image-lightbox';

import FallbackImg from '../../../assets/image-placeholder.svg';
import { LIGHTBOX_Z_INDEX } from '../../../constants/ui';

interface IProps {
  url: string;
  gallery?: string[];
  asImage?: boolean;
  alt?: string;
  preventDefault?: boolean;
  stopPropagation?: boolean;
  isScalable?: boolean;
  onImageError?: (url: string) => void;
  sx?: SxProps<Theme>;
}

// The library defaults the overlay to z-index 1000, under the drawers it is opened from.
const lightboxModalStyle = { overlay: { zIndex: LIGHTBOX_Z_INDEX } };

// [WARNING!] Not working good in React.Strict mode!
// https://github.com/frontend-collective/react-image-lightbox/issues/589
const LightboxBackgroundImage = React.forwardRef(
  (
    {
      url,
      gallery,
      asImage = false,
      alt = '',
      preventDefault = false,
      stopPropagation = false,
      isScalable = true,
      onImageError,
      sx = {},
    }: IProps,
    ref: React.Ref<HTMLDivElement | HTMLImageElement>,
  ) => {
    const theme = useTheme();

    const [isShown, setIsShown] = useState(false);

    const [failedUrl, setFailedUrl] = useState<string | null>(null);
    const isPlaceholder = !url || failedUrl === url;
    const src = isPlaceholder ? FallbackImg : url;
    const isZoomable = isScalable && !isPlaceholder;

    const handleImageError = useCallback(() => {
      if (!url) {
        return;
      }
      setFailedUrl(url);
      onImageError?.(url);
    }, [url, onImageError]);
    const images = useMemo(() => (gallery?.length ? gallery : [url]), [gallery, url]);
    const [index, setIndex] = useState(0);

    // Opening always starts on the image the user clicked, not wherever the slider was left.
    useEffect(() => {
      if (isShown) {
        setIndex(Math.max(0, images.indexOf(url)));
      }
    }, [isShown, images, url]);

    const hasSlider = images.length > 1;
    const nextSrc = useMemo(
      () => (hasSlider ? images[(index + 1) % images.length] : undefined),
      [hasSlider, images, index],
    );
    const prevSrc = useMemo(
      () => (hasSlider ? images[(index + images.length - 1) % images.length] : undefined),
      [hasSlider, images, index],
    );

    const handleMovePrev = useCallback(() => setIndex((i) => (i + images.length - 1) % images.length), [images.length]);
    const handleMoveNext = useCallback(() => setIndex((i) => (i + 1) % images.length), [images.length]);

    const handleEventPropagation = useCallback(
      (event: React.MouseEvent) => {
        if (preventDefault) {
          event.preventDefault();
        }
        if (stopPropagation) {
          event.stopPropagation();
        }
      },
      [preventDefault, stopPropagation],
    );

    const handleOpenLightbox = useCallback(
      (event: React.MouseEvent) => {
        if (event) {
          handleEventPropagation(event);
        }

        setIsShown(true);
      },
      [handleEventPropagation],
    );

    const handleCloseLightbox = useCallback(
      (event?: React.MouseEvent) => {
        if (event) {
          handleEventPropagation(event);
        }

        setIsShown(false);
      },
      [handleEventPropagation],
    );

    // react-image-lightbox reports a failed load with the image url, not an event.
    const handleImageLoadError = useCallback(() => setIsShown(false), []);

    const handleOverlayClick = useCallback(
      (event: React.MouseEvent) => {
        if (stopPropagation) {
          event.stopPropagation();
        }
      },
      [stopPropagation],
    );

    useEffect(() => {
      document.body.style.overflow = isShown ? 'hidden' : '';

      return () => {
        document.body.style.overflow = '';
      };
    }, [isShown]);

    const imageLayout = useMemo(() => {
      if (asImage) {
        return (
          <Box
            component="img"
            ref={ref as React.RefObject<HTMLImageElement>}
            onClick={isZoomable ? handleOpenLightbox : undefined}
            onError={handleImageError}
            src={src}
            alt={alt}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              // Non-scalable images inherit the cursor, so a clickable card keeps its pointer.
              '&:hover': {
                cursor: isZoomable ? 'zoom-in' : 'inherit',
              },
              ...sx,
            }}
          />
        );
      }

      return (
        <Box
          component="div"
          ref={ref as React.RefObject<HTMLDivElement>}
          onClick={isPlaceholder ? undefined : handleOpenLightbox}
          sx={{
            backgroundImage: `url("${src}")`,
            backgroundPosition: 'center',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            ...sx,
          }}
        />
      );
    }, [asImage, src, isPlaceholder, isZoomable, handleOpenLightbox, handleImageError, sx, ref, alt]);

    return (
      <>
        {imageLayout}
        {isShown && (
          <>
            <GlobalStyles
              styles={{
                '.ril__caption, .ril__toolbar': {
                  backgroundColor: 'transparent !important',
                },
                '.ril__image': {
                  backgroundColor: theme.palette.common.white,
                },
              }}
            />
            <Box onClick={handleOverlayClick}>
              <Lightbox
                mainSrc={images[index]}
                nextSrc={nextSrc}
                prevSrc={prevSrc}
                onMovePrevRequest={handleMovePrev}
                onMoveNextRequest={handleMoveNext}
                enableZoom={false}
                onCloseRequest={handleCloseLightbox}
                onImageLoadError={handleImageLoadError}
                reactModalStyle={lightboxModalStyle}
              />
            </Box>
          </>
        )}
      </>
    );
  },
);

export default LightboxBackgroundImage;
